import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  activeProcessSF,
  getActiveProcessKey,
  handleProcess,
  RydeProcessTitle,
  RydeUiUnit,
  supportsBulkSummaryToast,
  useProcesses,
} from "@ryde/shared";

import {
  useWatchBackupCreate,
  useWatchBackupRestore,
  useWatchBulkBookingCancel,
  useWatchBulkBookingClone,
  useWatchBulkFetchTrackingHistory,
  useWatchBulkFindArea,
  useWatchBulkImport,
  useWatchBulkJourneyAssign,
  useWatchBulkJourneyClone,
  useWatchBulkNotificationMarkAsRead,
  useWatchBulkNotificationResolve,
  useWatchBulkPriceRateUpdate,
  useWatchBulkProcessCompletion,
  useWatchBulkRecalculateWalkingDistance,
  useWatchBulkRideArchive,
  useWatchBulkRideCancel,
  useWatchBulkRideClone,
  useWatchBulkRideRebuild,
  useWatchBulkRideRebuildRoute,
  useWatchBulkRideRecalculatePrice,
  useWatchBulkRideRecalculateSupplierPrice,
  useWatchBulkRideReject,
  useWatchBulkRideSendSMS,
  useWatchBulkRideStatusDone,
  useWatchBulkRideStatusDoneApproved,
  useWatchBulkRideStatusNew,
  useWatchBulkRideStatusReceived,
  useWatchBulkRideUpdateStations,
  useWatchBulkSendToSupplier,
  useWatchBulkSetNearby,
} from "./processes";
import { useProcessBulkWatchersEnd } from "./use-process-bulk-watchers.end";

export const useProcessBulkWatchers = ({
  entity,
  currentEntityPage,
  avoidResetSelectedIds,
}: {
  entity: RydeUiUnit;
  currentEntityPage?: RydeUiUnit;
  avoidResetSelectedIds?: boolean;
}) => {
  const { t } = useTranslation();

  const {
    onArchiveComplete,
    onUpdateComplete,
    addressedFieldsValueOnCompleted,
  } = useProcessBulkWatchersEnd({
    entity,
    currentEntityPage,
    avoidResetSelectedIds,
  });

  const bulkUpdateProcessKey = getActiveProcessKey(
    RydeProcessTitle.BulkUpdate,
    entity,
  );
  const bulkArchiveProcessKey = getActiveProcessKey(
    RydeProcessTitle.BulkArchive,
    entity,
  );
  const bulkUpdateProcess = useRecoilValue(
    activeProcessSF(bulkUpdateProcessKey),
  );
  const bulkArchiveProcess = useRecoilValue(
    activeProcessSF(bulkArchiveProcessKey),
  );

  const { stopProcessPolling } = useProcesses();

  // Watch bulk update
  useEffect(() => {
    handleProcess({
      entity,
      process: bulkUpdateProcess,
      onComplete: onUpdateComplete,
      translationFunction: t,
      stopProcessPolling,
      processTitle: RydeProcessTitle.BulkUpdate,
      toastSummaryOnComplete: supportsBulkSummaryToast(entity),
    });
  }, [bulkUpdateProcess, entity, onUpdateComplete, stopProcessPolling, t]);

  // Bulk archive for rides is handled by useWatchBulkRideArchive (hub-aware toast via relatedHubRideIds).
  // Skip rides here so the same BulkArchive process is not completed twice — generic onArchiveComplete
  // would show ENTITY_SUCCESSFULLY_ARCHIVED while the ride hook shows ridesArchived / ridesArchivedWithRelatedHub.
  useEffect(() => {
    if (entity === "ride") {
      return;
    }

    handleProcess({
      entity,
      process: bulkArchiveProcess,
      onComplete: onArchiveComplete,
      translationFunction: t,
      stopProcessPolling,
      processTitle: RydeProcessTitle.BulkArchive,
      toastSummaryOnComplete: supportsBulkSummaryToast(entity),
    });
  }, [bulkArchiveProcess, entity, onArchiveComplete, stopProcessPolling, t]);

  useWatchBulkSendToSupplier({ entity, addressedFieldsValueOnCompleted });
  useWatchBulkFetchTrackingHistory({ entity, addressedFieldsValueOnCompleted });
  useWatchBulkFindArea({ entity, addressedFieldsValueOnCompleted });
  useWatchBulkNotificationResolve({ entity, addressedFieldsValueOnCompleted });
  useWatchBulkPriceRateUpdate({ entity, addressedFieldsValueOnCompleted });
  useWatchBulkBookingCancel({ entity, addressedFieldsValueOnCompleted });
  useWatchBulkBookingClone({ entity, addressedFieldsValueOnCompleted });
  useWatchBulkRideArchive({ entity, addressedFieldsValueOnCompleted });
  useWatchBulkRideCancel({ entity, addressedFieldsValueOnCompleted });
  useWatchBulkRideClone({ entity, addressedFieldsValueOnCompleted });
  useWatchBulkJourneyClone({ entity });
  useWatchBulkJourneyAssign({ entity, addressedFieldsValueOnCompleted });
  useWatchBulkRideReject({ entity, addressedFieldsValueOnCompleted });
  useWatchBulkImport({ entity });
  useWatchBulkRideSendSMS({ entity, addressedFieldsValueOnCompleted });
  useWatchBulkRideStatusNew({ entity, addressedFieldsValueOnCompleted });
  useWatchBulkRideStatusDone({ entity, addressedFieldsValueOnCompleted });
  useWatchBulkSetNearby({ entity, addressedFieldsValueOnCompleted });
  useWatchBulkRideRebuild({ entity, addressedFieldsValueOnCompleted });
  useWatchBulkRideUpdateStations({ entity, addressedFieldsValueOnCompleted });
  useWatchBulkRecalculateWalkingDistance({
    entity,
    addressedFieldsValueOnCompleted,
  });
  useWatchBulkRideStatusDoneApproved({
    entity,
    addressedFieldsValueOnCompleted,
  });
  useWatchBulkRideStatusReceived({
    entity,
    addressedFieldsValueOnCompleted,
  });
  useWatchBulkNotificationMarkAsRead({
    addressedFieldsValueOnCompleted,
    entity,
  });
  useWatchBulkRideRebuildRoute({
    entity,
    addressedFieldsValueOnCompleted,
  });
  useWatchBulkRideRecalculatePrice({
    entity,
    addressedFieldsValueOnCompleted,
  });
  useWatchBackupCreate({ entity });
  useWatchBackupRestore({ entity });
  useWatchBulkProcessCompletion({
    entity: "course",
    processTitle: RydeProcessTitle.CourseDeepRecalculate,
    addressedFieldsValueOnCompleted,
  });
  useWatchBulkProcessCompletion({
    entity: "priceRate",
    processTitle: RydeProcessTitle.CopyToSupplierPriceRates,
    addressedFieldsValueOnCompleted,
  });
  useWatchBulkRideRecalculateSupplierPrice({
    entity,
    addressedFieldsValueOnCompleted,
  });
};
